import type { ILayerOriginInfo, ILayerProps } from '@/version-types';

export interface IService {
    /**
     * 初始化或重置服务数据
     *
     * 业务每次显式调用 `editor.init()` 时执行，不会随编辑器挂载自动执行。
     */
    onInit: () => void;
    /**
     * 创建依赖编辑器配置的服务实例
     *
     * 首次调用 `editor.mount()` 时执行一次，容器校验通过后、`onMount` 之前触发。
     */
    onCreate?: () => void;
    /**
     * 激活与 DOM 挂载期间相关的资源
     *
     * 每次调用 `editor.mount()` 都会执行；首次晚于 `onCreate`，且位于编辑器内置 React root 渲染逻辑之前。
     */
    onMount?: () => void;
    /**
     * 释放与 DOM 挂载期间相关的资源
     *
     * 每次调用 `editor.unmount()` 都会执行；`editor.destroy()` 也会调用 `unmount()`。
     * 位于编辑器内置 React root 卸载逻辑之前。
     */
    onUnmount?: () => void;
    /**
     * 绑定服务级事件
     *
     * `ServiceManager` 构造期间、所有服务实例创建完成后执行一次，早于其他生命周期和业务配置写入。
     */
    onBindEvent?: () => void;
}

export interface IReplaceLayerResult {
    activeId?: string;
    wrapId?: string;
    newIds: string[];
    oldInfo: { index: number; layer: ILayerProps }[];
}

export interface IStageBaseOperateData {
    oldIds: number[];
    oldIndex: number;
    newIds: number[];
    newIndex: number;
    oldStageSelection?: number[];
    newStageSelection?: number[];
}

export interface IActiveInfo {
    info: ILayerOriginInfo;
    childInfoMap: Map<string, ILayerOriginInfo>;
}

export interface IExportLayerOriginInfo {
    originInfo: ILayerOriginInfo | undefined;
    childOriginInfoMap: Map<string, ILayerOriginInfo>;
}

/** 全局遮罩参数 */
export type IGlobalMask = {
    /** 是否显示加载中icon */
    loading?: boolean;
    /** 加载中文字 */
    text?: string;
    /** 是否显示背景遮罩 */
    background?: boolean | string;
};

/** 进度配置接口 */
export interface ProgressConfig {
    type: 'progress'; // 标识为进度条类型
    baseTime: number; // 基准时间（分钟），前99%的进度时间
    textOnly?: boolean; // 新增：是否只显示纯文本进度
}

/** extraInfo 类型：支持字符串或进度配置 */
export type LoadingExtraInfo = string | ProgressConfig;

/** 判断是否为进度配置 */
export const isProgressConfig = (info: LoadingExtraInfo): info is ProgressConfig => {
    return typeof info === 'object' && info !== null && 'type' in info && info.type === 'progress';
};

/** 删除数据 */
export interface IDelLayerData {
    item: {
        /** 新图层 */
        newLayer?: ILayerProps;
        /** 旧图层 */
        oldLayer: ILayerProps;
        /** 旧图层索引 */
        oldLayerIndex: number;
        /** 替换的图层id */
        replaceLayerId: string;
        /** 删除的图层id */
        delId: string;
        /** 子图层索引 */
        childIndexes: number[];
        /** 子图层id */
        childIds: string[];
        /** 是否删除所有 */
        delAll: boolean;
    };
    operate: {
        /** 激活图层id */
        activeId: string;
    };
}

export enum MoveListenerLevel {
    /** 最优先的等级, 只可做取值操作, 不可有修改操作 */
    PREPARE_LEVEL = 1000,
    /** 默认等级 */
    DEFAULT_LEVEL = 0,
}

export interface ICustomNodeData {
    /** 原始索引 */
    frameId: number;
    /** 类型 */
    type: string;
    /** 节点 */
    node: React.ReactNode;
    /** 位置 */
    position: CustomNodePosition;
    /** 自动位置 */
    autoPosition: boolean;
}

export type CustomNodePosition = 'top' | 'bottom';

/** 获取源图片接口 */
export interface IGetSrcImage {
    getSrcImage(): Promise<HTMLImageElement>;
}

/** 设置资源接口 */
export interface ISetResource {
    setResource(baseImage: HTMLImageElement | HTMLCanvasElement): void;
}

/** 清除 edgeless 预览资源接口（滤镜/调色预览缓存） */
export interface IEdgelessPreviewClearable {
    /**
     * 清除本地预览资源
     *
     * @param options - 可选；`rerender` 控制清理后是否立即按正式图层重新渲染，默认 true
     */
    clearPreviewResource(options?: { rerender?: boolean }): void;
}
